import React, { useState } from 'react'
import classNames from 'classnames'
import FloatingModal from '@components/modals/FloatingModal'
import { containers as photoLibraryContainers } from '@features/photo_library'
import ProgressIndicator from '../ProgressIndicator'
import Button from '@components/Button'
import Image from '@components/Image'
import SVGIcon from '@components/SVGIcon'
import { Photo, isJsonapiPhoto, isRestPhoto, PhotoStyles } from '@app-types/entity_types'

type ChangeObject = {
  name: string
  value: any
}

type MediaPickerProps = {
  name: string
  photo?: Photo | null
  onChange: (data: ChangeObject | null) => void
  thumbnailStyle: PhotoStyles
  apiVersion: number
}

type InteractiveEvent = React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>

const MediaPicker: React.FC<MediaPickerProps> = ({
  name = 'media',
  photo = null,
  onChange,
  thumbnailStyle = 'small_landscape',
  apiVersion = 1,
}) => {
  const { PhotoLibraryContainer } = photoLibraryContainers

  const [photoLibraryVisible, setPhotoLibraryVisible] = useState(false)

  const handleChangeClick = (e: InteractiveEvent) => {
    e.preventDefault()
    showPhotoLibrary()
  }

  const handleClearClick = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
    e.preventDefault()
    dispatchChange(null)
    hidePhotoLibrary()
  }

  const handlePhotoSelect = (id: string) => {
    dispatchChange(id)
    hidePhotoLibrary()
  }

  // dispatches a change so the container form can get notified when this component's value changes
  const dispatchChange = (value: string | null) => {
    onChange({
      name,
      value,
    })
  }

  const hidePhotoLibrary = () => setPhotoLibraryVisible(false)
  const showPhotoLibrary = () => setPhotoLibraryVisible(true)

  const getImageContent = () => {
    let content
    if (photo === null || photo === undefined) {
      // no photo selected yet
      content = (
        <div className="media-picker__image-placeholder">
          <SVGIcon
            xlinkHref="#streamline-edit-image-photo"
            alt="Placeholder image"
            pointerEvents="none"
            className="no-fill"
          />
        </div>
      )
    } else {
      const isProcessing =
        (isRestPhoto(photo) && photo.image_processing_complete === false) ||
        (isJsonapiPhoto(photo) && photo.attributes.image_processing_complete === false)

      // photo selected/uploaded, but still processing
      if (isProcessing) {
        content = <ProgressIndicator hint="Processing" alwaysHint={true} />
      } else {
        // Determine the image URL based on the photo type
        const imageUrls = isRestPhoto(photo) ? photo.image_urls : photo.attributes.image_urls
        // Photo selected and it is finished processing
        const thumbnailProps = imageUrls[thumbnailStyle]
        // Ensure thumbnailProps exists and has a `src` property
        if (thumbnailProps && 'src' in thumbnailProps) {
          const { src, width, height } = thumbnailProps
          content = <Image src={src} width={width} height={height} className="media-picker__image" />
        }
      }
    }
    return (
      <div
        className="media-picker__preview-container"
        tabIndex={0}
        onClick={handleChangeClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            // Trigger on Enter or Space
            e.preventDefault()
            handleChangeClick(e)
          }
        }}
        role="button"
        aria-label="Choose a photo"
      >
        {content}
      </div>
    )
  }

  const getClearContent = () => {
    if (photo) {
      return (
        <Button
          className="button-reset top-right-cross"
          onClick={handleClearClick}
          title="Clear seelcted photo"
          aria-label="Clear selected photo"
        >
          <SVGIcon xlinkHref="#streamline-delete-1" className="icon-shadow" pointerEvents="none" alt="Close" />
        </Button>
      )
    }
    return null
  }

  const imageContent = getImageContent()
  const clearButton = getClearContent()
  const className = classNames('media-picker', `media-picker--${thumbnailStyle}`)
  return (
    <div className={className} data-testid="media-picker">
      <div className="media-picker__content">
        {imageContent}
        {clearButton}
      </div>
      <FloatingModal title="Choose image" show={photoLibraryVisible} onClose={hidePhotoLibrary}>
        <PhotoLibraryContainer onSelectItem={handlePhotoSelect} apiVersion={apiVersion} />
      </FloatingModal>
    </div>
  )
}

export default MediaPicker
